'use client'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faPlayCircle, faMicrophone, faDice } from '@fortawesome/free-solid-svg-icons'
import Link from 'next/link'
import { generateMatchUrl } from '@/lib/slug-utils'
import { useLiveScore } from '@/hooks/useLiveScore'
import { Match } from '@/lib/database'

interface LiveMatchCardProps {
  match: Match
  isLoaded?: boolean
}

export default function LiveMatchCard({ match, isLoaded = false }: LiveMatchCardProps) {
  // Use live score hook
  const { getCurrentScore, loading: scoreLoading } = useLiveScore({
    matchId: match.id,
    id_api: match.id_api,
    match_status: match.match_status,
    home_score: match.home_score,
    away_score: match.away_score,
    autoRefresh: true,
    refreshInterval: 30000 // 30 seconds for live matches
  })

  const currentScore = getCurrentScore()
  const isLive = match.match_status === '3'
  const isFinished = match.match_status === '2' || match.match_status === '4'
  const isUpcoming = match.match_status === '1'

  const getStatusDisplay = () => {
    if (isLive) {
      return { text: 'LIVE', className: 'bg-[#dc2626] text-white' }
    }
    if (isFinished) {
      return { text: 'Kết thúc', className: 'bg-[#666] text-white' }
    }
    return { text: 'Sắp diễn ra', className: 'bg-[#1a1a1a] text-[#999]' }
  }

  const statusDisplay = getStatusDisplay()

  // Format match time
  const formatMatchTime = (timeStart: string) => {
    if (!timeStart) return ''
    return new Date(timeStart).toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Generate match URL
  const matchUrl = generateMatchUrl(
    match.id,
    match.team_hone || 'Đội nhà',
    match.team_away || 'Đội khách',
    match.league_name,
    match.time_start
  )

  return (
    <div className="block relative bg-[#2a2a2a] rounded-lg overflow-hidden border border-white/5 transition-all hover:border-[var(--primary)] hover:shadow-lg hover:shadow-[var(--primary)]/20 group">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{
          backgroundImage: `url('/images/bg-cards.webp')`
        }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header - Make clickable */}
        <Link 
          href={matchUrl}
          className="block"
        >
          <div className="flex justify-between items-center p-4 border-b border-white/10 hover:bg-white/5 transition-colors cursor-pointer">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faClock} className="text-[#999] text-xs" />
              <span className="text-[#999] text-sm">
                {match.time_start ? formatMatchTime(match.time_start) : ''}
              </span>
            </div>
            <div className={`text-xs px-3 py-1 rounded-full ${statusDisplay.className}`}>
              {statusDisplay.text}
            </div>
          </div>
        </Link>

        {/* Teams and Score - Make clickable */}
        <Link 
          href={matchUrl}
          className="block hover:bg-white/5 transition-colors cursor-pointer"
        >
          <div className="p-4 border-b border-white/10">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              {/* Home Team */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {match.team_hone_avatar && (
                    <img
                      src={match.team_hone_avatar.startsWith('http') ? match.team_hone_avatar : `https://okapi999.com${match.team_hone_avatar}`}
                      alt={match.team_hone || 'Đội nhà'}
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  )}
                  <span className="text-white text-sm truncate block">
                    {match.team_hone || 'Đội nhà'}
                  </span>
                </div>
              </div>

              {/* Score */}
              <div className="flex items-center gap-3">
                {scoreLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-600 rounded animate-pulse"></div>
                    <span className="text-[#999]">-</span>
                    <div className="w-6 h-6 bg-gray-600 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <>
                    <span className={`text-lg font-bold ${isLive ? 'text-[var(--primary)]' : 'text-[var(--primary)]'}`}>
                      {currentScore.home_score ?? (isFinished ? '0' : '--')}
                    </span>
                    <span className="text-[#999]">-</span>
                    <span className={`text-lg font-bold ${isLive ? 'text-[var(--primary)]' : 'text-[var(--primary)]'}`}>
                      {currentScore.away_score ?? (isFinished ? '0' : '--')}
                    </span>
                  </>
                )}
              </div>

              {/* Away Team */}
              <div className="min-w-0 text-right">
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-white text-sm truncate block">
                    {match.team_away || 'Đội khách'}
                  </span>
                  {match.team_away_avatar && (
                    <img
                      src={match.team_away_avatar.startsWith('http') ? match.team_away_avatar : `https://okapi999.com${match.team_away_avatar}`}
                      alt={match.team_away || 'Đội khách'}
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* League Info */}
          <div className="px-4 py-2 border-b border-white/10">
            <span className="text-[#999] text-xs">
              {match.league_name || 'Giải đấu không xác định'}
            </span>
          </div>
        </Link>

        {/* Footer Info - Keep buttons outside of Link */}
        <div className="p-4">
          {/* Live/Watch and Betting Buttons */}
          <div className="mb-3 grid grid-cols-2 gap-2">
            {/* Live/Watch Button */}
            <Link 
              href={matchUrl}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isLive 
                  ? 'bg-[var(--button-xem-live)] text-white hover:bg-[color-mix(in_srgb,var(--button-xem-live),#000_10%)]' 
                  : isFinished 
                    ? 'bg-[var(--button-primary)] text-white hover:bg-[color-mix(in_srgb,var(--button-primary),#000_10%)]'
                    : 'bg-[var(--badge-chua-phat-song)] text-white hover:bg-[#78350f]'
              }`}
            >
              <FontAwesomeIcon icon={isLive ? faPlayCircle : (isFinished ? faPlayCircle : faClock)} />
              {isLive ? 'Xem Live' : (isFinished ? 'Xem Lại' : 'Chưa phát sóng')}
            </Link>

            {/* Betting Button */}
            <a 
              href="http://bit.ly/4erM1hn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all bg-[#dc2626] text-white hover:bg-[#b91c1c]"
            >
              <FontAwesomeIcon icon={faDice} />
              Chiến 789win
            </a>
          </div>

          {/* Commentary Info */}
          {(isLive || isUpcoming) && match.caster && match.caster.trim() !== '' && (
            <div className="flex flex-wrap gap-2">
              {match.caster.split(',').map((caster, index) => (
                <div key={index} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/30 text-white text-xs">
                  <FontAwesomeIcon icon={faMicrophone} className="text-[var(--primary)]" />
                  <span>BLV: {caster.trim()}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Fallback BLV indicator if no specific caster */}
          {(isLive || isUpcoming) && (!match.caster || match.caster.trim() === '') && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/30 text-[#999] text-xs w-fit">
              <FontAwesomeIcon icon={faMicrophone} className="text-[var(--primary)]" />
              <span>Có BLV</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 